/**
 * Sponsor picker — select-with-add-new for the sponsor field.
 *
 * Pattern mirrors the department picker on the project detail page: a
 * real <select> over the sponsors already in the system, with an
 * "+ Add new…" sentinel that flips the field to a text input so a
 * brand-new sponsor name can be introduced inline.
 *
 * Source of truth for the option list is project.sponsor across all
 * rows, de-duped and sorted.
 *
 * Why not PersonPicker? PersonPicker is a typeahead anchored to the
 * roster (engineers, PMs, etc.). Sponsors are usually business owners
 * who aren't on the IT roster, so the typeahead surfaced the wrong
 * suggestions and free-text typing dominated. A dropdown of "people
 * who have been sponsors before" is the right signal.
 */
import { useMemo, useState } from "react";
import { usePortfolio } from "@/hooks/usePortfolio";

const inputCls =
  "rounded-md border border-line bg-surface px-2 py-1 text-xs text-ink focus:border-brand-navy-400 focus:outline-none focus:ring-1 focus:ring-brand-navy-300";

export function SponsorPicker({
  value,
  onChange,
  className,
  autoFocus = true,
  onCancel,
}: {
  value: string | null;
  onChange: (next: string | null) => void;
  className?: string;
  autoFocus?: boolean;
  /** Called when the user backs out of "add new" mode without saving.
   *  Optional — parent uses it to collapse the editor row. */
  onCancel?: () => void;
}) {
  const [addingNew, setAddingNew] = useState(false);
  const portfolio = usePortfolio(false);

  const options = useMemo(() => {
    const set = new Set<string>();
    for (const p of portfolio.data ?? []) {
      const v = (p.sponsor ?? "").trim();
      if (v) set.add(v);
    }
    return Array.from(set).sort();
  }, [portfolio.data]);

  if (addingNew) {
    return (
      <div className="flex items-center gap-2">
        <input
          autoFocus={autoFocus}
          type="text"
          placeholder="New sponsor name"
          defaultValue=""
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              const v = (e.target as HTMLInputElement).value.trim();
              onChange(v || null);
              setAddingNew(false);
            } else if (e.key === "Escape") {
              setAddingNew(false);
              onCancel?.();
            }
          }}
          onBlur={(e) => {
            const v = e.target.value.trim();
            onChange(v || null);
            setAddingNew(false);
          }}
          className={inputCls}
        />
        <button
          type="button"
          onClick={() => {
            setAddingNew(false);
            onCancel?.();
          }}
          className="text-[11px] text-ink-muted hover:text-ink-2"
        >
          Cancel
        </button>
      </div>
    );
  }

  return (
    <select
      autoFocus={autoFocus}
      value={value ?? ""}
      onChange={(e) => {
        if (e.target.value === "__add_new__") {
          setAddingNew(true);
        } else {
          onChange(e.target.value || null);
        }
      }}
      className={className ?? inputCls}
    >
      <option value="">— none —</option>
      {options.map((s) => (
        <option key={s} value={s}>
          {s}
        </option>
      ))}
      {value && !options.includes(value) && (
        <option value={value}>{value}</option>
      )}
      <option value="__add_new__">+ Add new…</option>
    </select>
  );
}
